/**
 * Lightweight "What's new" briefing rendered as a Dashboard tab.
 * Audience: end users (Account Managers, Team Leads, internal staff).
 * Tone: plain English — what changed and what to do with it.
 *
 * Keep this short. Bigger announcements belong in release notes.
 */
import { BRAND } from "../../../branding";

const ITEMS = [
  {
    key:   "csv-export",
    badge: "NEW",
    title: "Tasks & Defects — Export to CSV",
    body: [
      "The Export button on the Tasks and Defects screens now downloads the full matching result set as a CSV, not just the page currently on screen.",
    ],
    bullets: [
      "Respects whatever filters, search, and sort you have applied.",
      "No row limit — every matching record is included, however many there are.",
    ],
  },
  {
    key:   "attachments-download",
    badge: "FIXED",
    title: "Attachments — downloads now work for everyone",
    body: [
      "Files attached to Tasks, Defects, Features, Sub-Features, Dependencies, Meetings, and Product Releases are now streamed through ProTrack itself instead of linking straight to SharePoint.",
    ],
    bullets: [
      "Fixes downloads that used to silently fail for external / customer users.",
      "Click a file name — it still opens in a new tab, just more reliably.",
    ],
  },
  {
    key:   "efforts-by-customer",
    badge: "NEW",
    title: "Reports → Efforts by Customer, now clickable",
    body: [
      "The Efforts by Customer chart in Reports is now a drill-down, not just a summary.",
    ],
    bullets: [
      "Click a customer's bar to break that customer's effort down by user.",
      "Toggle between a table and a bar-chart view inside the drill-down.",
    ],
  },
  {
    key:   "task-estimate-required",
    badge: "IMPROVED",
    title: "Task creation now asks for Estimated Hours",
    body: [
      "Creating a new task requires an Estimated Hours value before it can be saved.",
    ],
    bullets: [
      "Keeps effort tracking and downstream reporting accurate from the moment a task is created.",
    ],
  },
  {
    key:   "timesheet-picker-scope",
    badge: "FIXED",
    title: "Timesheet — entry picker now finds all your work",
    body: [
      "The task/defect picker inside a Timesheet entry now reliably shows everything assigned to you or your team.",
    ],
    bullets: [
      "Previously it could come up empty for some users depending on their project access setup.",
    ],
  },
  {
    key:   "user-not-found-fixes",
    badge: "FIXED",
    title: "Fixed occasional \"User not found\" save errors",
    body: [
      "Some users could intermittently hit a \"User not found\" error when saving — on Todos, Dependencies, Discussions, Defects, Features, Sub-Features, Appraisals, Product Releases, and Attachments.",
      "This was caused by a login-matching quirk unrelated to your account, and is now fixed across all of these screens.",
    ],
  },
  {
    key:   "epics",
    badge: "NEW",
    title: "Epics — group your work end-to-end",
    body: [
      "An Epic is a release-scoped container that ties together tasks, release plans, " +
      "implementation checklists, and the teams delivering them. Think of it as the " +
      "answer to “what's in the next release for this customer?”",
      "Open the Epics page (left sidebar) to:",
    ],
    bullets: [
      "Create an Epic per customer + project + release.",
      "Attach Tasks to the Epic — every new task now asks for an Epic so nothing slips through the cracks.",
      "Track release readiness through the Checklist tab (Dev → Regression → UAT → Staging → Live).",
      "See which teams (API / Web / Mobile / QA / etc.) have signed off via the Team Plan tab.",
    ],
    highlight: "*** If you face any issue on this, reach out to your AM. ***",
    footnote: "Access: Admin and Account Managers (internal).",
  },
  {
    key:   "presales",
    badge: "NEW",
    title: "Pre-Sales — the deal pipeline",
    body: [
      "A focused CRM for leads we are working on before they become projects. " +
      "Each lead tracks contact, company, owner, pricing, scope of work, milestones, reminders, and spend.",
    ],
    bullets: [
      "Drag-free stage stepper: New → Qualified → Demo FN → Demo Tech → Negotiation, with Won / Lost buttons in the footer.",
      "Pricing tab stores List / Offered / Agreed in rupees and shows the discount % live.",
      "Scope tab now supports adding SOW products and product-specific modules directly from the drawer.",
      "Module scope status rotates on click: In Scope → Optional → Out.",
      "Reminders now show relative due labels like Tomorrow / in 4d / 3d overdue.",
    ],
    footnote: "Access: Admin users only.",
  },
  {
    key:   "features",
    badge: "REFRESHED",
    title: "Features view — same data, V2 styling",
    body: [
      "The Feature Master page has been rebuilt to match the look and feel of Tasks and Defects.",
    ],
    bullets: [
      "Expand a feature row to see its sub-features inline (no more separate page).",
      "Click any row → side drawer opens with Overview / Discussions / Attachments tabs.",
      "“+ New Feature” and “↑ Import Excel” now open side drawers instead of modals.",
      "Deep-links from the Defects screen jump straight into the relevant feature in edit mode.",
    ],
  },
  {
    key:   "myview",
    badge: "NEW",
    title: "Dashboard → My View & Team Lead View",
    body: [
      "Two tabs on the Dashboard surface what's actually on your plate this week.",
    ],
    bullets: [
      "My View: your overdue tasks + defects, what's due today, what's upcoming.",
      "Team Lead View (if you lead any team): the same six lists, but rolled up across every member of every team you lead — with an Assignee column so you know who owns each item.",
      "Click any KPI tile to switch the detail table below it.",
    ],
  },
  {
    key:   "meetings",
    badge: "IMPROVED",
    title: "Meetings — edit from the side drawer",
    body: [
      "Internal meetings can now be edited without leaving the page. " +
      "Open any meeting → click ✏️ Edit → change title, time, participants, project, or status, then Save Changes.",
    ],
    bullets: [
      "Only the meeting organiser and Admins see the Edit button.",
      "Adding or removing a participant no longer resets attendance for the others.",
    ],
  },
];

export default function WhatsNew({ user }) {
  return (
    <div className="wn-wrap">
      <div className="wn-hero">
        <div className="wn-eyebrow">Product Updates</div>
        <h2 className="wn-headline">What's new in ProTrack</h2>
        <p className="wn-lede">
          A quick tour of what's changed recently and what's worth trying first.
          Pick a card to read the highlights — open the relevant page from the left sidebar to dig in.
        </p>
      </div>

      <div className="wn-grid">
        {ITEMS.map((it) => (
          <article key={it.key} className="wn-card">
            <div className="wn-card-head">
              <span className={`wn-badge wn-badge--${it.badge.toLowerCase()}`}>{it.badge}</span>
              <h3 className="wn-card-title">{it.title}</h3>
            </div>
            {it.body.map((para, i) => (
              <p key={i} className="wn-card-body">{para}</p>
            ))}
            {it.bullets && (
              <ul className="wn-card-list">
                {it.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
            {it.highlight && (
              <div className="wn-card-highlight">
                <strong>{it.highlight}</strong>
              </div>
            )}
            {it.footnote && <div className="wn-card-foot">{it.footnote}</div>}
          </article>
        ))}
      </div>

      <div className="wn-tip">
        Have feedback or a feature you'd like to see? Ping{" "}
        <strong>{user?.name || "your account team"}</strong>
        {BRAND.supportEmail
          ? <> or write to <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>.</>
          : "."}
      </div>
    </div>
  );
}
